import { useForm, SubmitHandler } from "react-hook-form";
import { useEffect, useMemo, useState, useCallback } from "react";
import emailjs from "@emailjs/browser";
import {
  Mail, MessageSquare, User, Send, CheckCircle2, XCircle, Loader2,
  Shield, Globe, Instagram,
} from "lucide-react";
import i18n from "../i18n";

type FormInputs = {
  name: string;
  subject: string;
  email: string;
  message: string;
  hpField?: string;   // honeypot (nom volontairement générique, pas "website"/"company" — trop souvent auto-rempli par les navigateurs)
  startedAt?: string; // anti-bot temporel
};

// Icône Discord inline
const DiscordIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 245 240" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M104.4 104.5c-5.7 0-10.2 5-10.2 11.1 0 6.1 4.6 11.1 10.2 11.1 5.7 0 10.2-5 10.2-11.1.1-6.2-4.5-11.1-10.2-11.1zm36.2 0c-5.7 0-10.2 5-10.2 11.1 0 6.1 4.6 11.1 10.2 11.1s10.2-5 10.2-11.1c0-6.2-4.5-11.1-10.2-11.1z"/>
    <path d="M189.5 20h-134C38.6 20 30 28.6 30 38.5v163c0 9.9 8.6 18.5 18.5 18.5h114l-5.4-18.8 13 12 12.3 11.4 21.7 19V38.5c0-9.9-8.6-18.5-18.6-18.5zm-35.1 131s-3.3-4-6-7.6c12-3.4 16.6-11 16.6-11-3.8 2.5-7.4 4.3-10.7 5.5-4.7 2-9.2 3.3-13.6 4-9 1.7-17.3 1.2-24.4-.1-5.4-1-10-2.5-13.9-4-2.2-.9-4.6-2-7-3.5-.3-.2-.7-.4-1-.6-.2-.1-.3-.2-.4-.3-.6-.3-.9-.5-.9-.5s4.4 7.3 16 10.8c-2.7 3.6-6.1 7.9-6.1 7.9-20.1-.6-27.7-13.9-27.7-13.9 0-29.5 13.2-53.4 13.2-53.4 13.2-9.9 25.8-9.6 25.8-9.6l.9 1c-16.6 4.7-24.2 12-24.2 12s2 .9 5.3 2.4c9.6 4.2 17.1 10.8 21 14.8 0 0 7.5-5.7 21.4-5.7s21.4 5.7 21.4 5.7c3.9-4 11.4-10.6 21-14.8 3.3-1.5 5.3-2.4 5.3-2.4s-7.6-7.3-24.2-12l.9-1s12.6-.3 25.8 9.6c0 0 13.2 23.9 13.2 53.4 0 .1-7.7 13.4-27.8 14z"/>
  </svg>
);

// Mini hook i18n safe (évite TS2589)
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key)),
    []
  );
  return { t, lng };
}

export const Contact = () => {
  const { t } = useI18nSafe();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm<FormInputs>({
    defaultValues: { name: "", subject: "", email: "", message: "" },
    mode: "onBlur",
  });

  const [status, setStatus] = useState<"idle" | "ok" | "err">("idle");

  // Anti-bot temporel : on fixe l'heure d'ouverture dans un champ caché
  useEffect(() => {
    const now = String(Date.now());
    document.querySelector<HTMLInputElement>("#startedAt")?.setAttribute("value", now);
  }, []);

  const msgValue = watch("message") || "";
  const messageCount = useMemo(() => msgValue.length, [msgValue]);

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    // Honeypots anti-bot : on feint un succès (ne pas indiquer au bot qu'il a
    // été détecté) plutôt que de ne rien afficher — un faux positif humain ne
    // doit jamais donner l'impression que le formulaire est cassé.
    const isHoneypotField = !!data.hpField && data.hpField.trim() !== "";
    const now = Date.now();
    const started = Number(data.startedAt || now);
    const isTooFast = now - started < 4000;
    if (isHoneypotField || isTooFast) {
      setStatus("ok");
      reset();
      return;
    }

    // ⚠️ Mets ces IDs dans des variables d'env si possible
    const serviceID = "service_j8zheoo";
    const templateID = "template_dftbvpw";
    const publicKey = "-r1PJXUeYYG6KEGQv";

    const templateParams = {
      // Noms attendus par le template EmailJS "Contact Us" (onglet Content) :
      // {{title}}, {{name}}, {{time}}, {{message}}. On garde aussi subject/email
      // (dont reply_to, pour pouvoir répondre directement au visiteur depuis l'email).
      title: data.subject,
      subject: data.subject,
      name: data.name,
      time: new Date().toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }),
      message: data.message,
      email: data.email,
      reply_to: data.email,
    };

    try {
      await emailjs.send(serviceID, templateID, templateParams, publicKey);
      setStatus("ok");
      reset();
    } catch (err) {
      console.error("EmailJS error:", err);
      setStatus("err");
    }
  };

  return (
    <section className="min-h-[calc(100vh-4rem)] w-full bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors">
      <div className="mx-auto max-w-6xl px-4 py-10 sm:py-12">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            <span className="text-orange-600 dark:text-orange-400">
              {t("contact:header.titlePrefix")}
            </span>{" "}
            {t("contact:header.titleBrand", { brand: "SwishData" })}
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
            {t("contact:header.subtitle")}
          </p>
        </div>

        {/* Grid: Info + Form */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Info panel */}
          <aside className="lg:col-span-2">
            <div className="h-full rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                <h2 className="text-lg font-semibold">{t("contact:info.commitmentTitle")}</h2>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {t("contact:info.commitmentBody")}
              </p>

              <div className="mt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="mt-0.5 h-5 w-5 text-gray-500 dark:text-gray-400" />
                  <div>
                    <p className="text-sm font-medium">{t("contact:info.emailLabel")}</p>
                    <a
                      href="mailto:contact@swishdata.app"
                      className="text-sm text-orange-600 dark:text-orange-400 hover:underline"
                    >
                      {t("contact:info.emailAddress")}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MessageSquare className="mt-0.5 h-5 w-5 text-gray-500 dark:text-gray-400" />
                  <div>
                    <p className="text-sm font-medium">{t("contact:info.discordLabel")}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t("contact:info.discordBody")}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Globe className="mt-0.5 h-5 w-5 text-gray-500 dark:text-gray-400" />
                  <div>
                    <p className="text-sm font-medium">{t("contact:info.docsLabel")}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t("contact:info.docsBody")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Réseaux */}
              <div className="mt-6 flex items-center gap-4">
                <a
                  aria-label="Discord"
                  href="https://discord.gg/q5zR75uTB6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full border border-gray-200 dark:border-gray-800 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                  title={t("contact:info.discordTitle")}
                >
                  <DiscordIcon className="h-5 w-5" />
                </a>
                <a
                  aria-label="Instagram"
                  href="https://www.instagram.com/swishdatafr/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full border border-gray-200 dark:border-gray-800 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                  title={t("contact:info.instagramTitle")}
                >
                  <Instagram className="h-5 w-5" />
                </a>
              </div>

              <div className="mt-6 rounded-xl bg-gray-50 dark:bg-gray-950/40 border border-gray-200 dark:border-gray-800 p-4 text-xs text-gray-600 dark:text-gray-400">
                {t("contact:info.tip")}
              </div>
            </div>
          </aside>

          {/* Form card */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 sm:p-8">
              {/* Status banner */}
              {status !== "idle" && (
                <div
                  className={`mb-6 flex items-start gap-3 rounded-xl border p-3 text-sm ${
                    status === "ok"
                      ? "border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-900/20 dark:text-green-300"
                      : "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300"
                  }`}
                  role="status"
                  aria-live="polite"
                >
                  {status === "ok" ? (
                    <>
                      <CheckCircle2 className="mt-0.5 h-5 w-5" />
                      <p>{t("contact:status.ok")}</p>
                    </>
                  ) : (
                    <>
                      <XCircle className="mt-0.5 h-5 w-5" />
                      <p>{t("contact:status.err")}</p>
                    </>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
                {/* Honeypots — hors-écran plutôt que display:none, et nom générique pour
                    éviter l'auto-remplissage navigateur (ex. "website"/"company" sont
                    reconnus par beaucoup de gestionnaires d'autofill même cachés). */}
                <div
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", top: "auto", width: 1, height: 1, overflow: "hidden" }}
                >
                  <input tabIndex={-1} autoComplete="off" {...register("hpField")} />
                </div>
                <input id="startedAt" tabIndex={-1} autoComplete="off" {...register("startedAt")} className="hidden" />

                {/* Nom */}
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium">
                    {t("contact:form.name")}
                  </label>
                  <div className="relative">
                    <User className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      {...register("name", {
                        required: t("contact:form.errors.nameRequired"),
                        minLength: { value: 2, message: t("contact:form.errors.nameMin") },
                        maxLength: { value: 80, message: t("contact:form.errors.nameMax") },
                      })}
                      id="name"
                      type="text"
                      placeholder={t("contact:form.placeholders.name")}
                      aria-invalid={!!errors.name || undefined}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900
                                 pl-10 pr-3 py-3 text-sm placeholder-gray-400 dark:placeholder-gray-500
                                 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
                  )}
                </div>

                {/* Sujet */}
                <div>
                  <label htmlFor="subject" className="mb-1.5 block text-sm font-medium">
                    {t("contact:form.subject")}
                  </label>
                  <div className="relative">
                    <User className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      {...register("subject", {
                        required: t("contact:form.errors.subjectRequired"),
                        minLength: { value: 3, message: t("contact:form.errors.subjectMin") },
                        maxLength: { value: 120, message: t("contact:form.errors.subjectMax") },
                      })}
                      id="subject"
                      type="text"
                      placeholder={t("contact:form.placeholders.subject")}
                      aria-invalid={!!errors.subject || undefined}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900
                                 pl-10 pr-3 py-3 text-sm placeholder-gray-400 dark:placeholder-gray-500
                                 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>
                  {errors.subject && (
                    <p className="mt-1 text-xs text-red-500">{errors.subject.message}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium">
                    {t("contact:form.email")}
                  </label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      {...register("email", {
                        required: t("contact:form.errors.emailRequired"),
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: t("contact:form.errors.emailInvalid"),
                        },
                      })}
                      id="email"
                      type="email"
                      inputMode="email"
                      placeholder={t("contact:form.placeholders.email")}
                      aria-invalid={!!errors.email || undefined}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900
                                 pl-10 pr-3 py-3 text-sm placeholder-gray-400 dark:placeholder-gray-500
                                 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <div className="flex items-center justify-between">
                    <label htmlFor="message" className="mb-1.5 block text-sm font-medium">
                      {t("contact:form.message")}
                    </label>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      {t("contact:counters.chars", { count: messageCount })}
                    </span>
                  </div>
                  <div className="relative">
                    <MessageSquare className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <textarea
                      {...register("message", {
                        required: t("contact:form.errors.messageRequired"),
                        minLength: { value: 10, message: t("contact:form.errors.messageMin") },
                        maxLength: { value: 1200, message: t("contact:form.errors.messageMax") },
                      })}
                      id="message"
                      rows={6}
                      placeholder={t("contact:form.placeholders.message")}
                      aria-invalid={!!errors.message || undefined}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900
                                 pl-10 pr-3 py-3 text-sm placeholder-gray-400 dark:placeholder-gray-500
                                 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-y"
                    />
                  </div>
                  {errors.message && (
                    <p className="mt-1 text-xs text-red-500">{errors.message.message}</p>
                  )}
                </div>

                {/* CTA */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-lg
                               bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold
                               px-5 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400
                               transition-colors"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t("contact:form.sending")}
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        {t("contact:form.send")}
                      </>
                    )}
                  </button>

                  <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                    {t("contact:form.sendProblem")}{" "}
                    <a className="underline" href="mailto:contact@swishdata.app">
                      {t("contact:form.sendFallback")}
                    </a>.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Footer slim */}
        <div className="mt-8 text-center text-xs text-gray-500 dark:text-gray-400">
          {t("contact:footer", { brand: "SwishData" })}
        </div>
      </div>
    </section>
  );
};
